import { useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const RESULTS = { verified: "Verified as accurate", updated: "Updated", deleted: "Deleted", no_response: "No response within 30 days", other: "Other" };
const sel = "min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 text-sm";

function AccountOutcome({ doc, account, recorded, onSaved }) {
  const [result, setResult] = useState(recorded?.result || "");
  const [date, setDate] = useState(recorded?.response_date || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/outcome`, { account_id: account.account_id, result, response_date: result === "no_response" ? null : date });
      toast.success("Response recorded");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record the response"); } finally { setBusy(false); }
  };
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`outcome-${account.account_id}`}>
      <div className="text-sm font-medium text-slate-900">{account.creditor || "Account"}</div>
      {recorded && <div className="text-xs text-emerald-700" data-testid={`outcome-recorded-${account.account_id}`}>Recorded: {RESULTS[recorded.result]}{recorded.response_date ? ` on ${recorded.response_date}` : ""}</div>}
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <select className={sel} value={result} onChange={(e) => setResult(e.target.value)} data-testid={`outcome-result-${account.account_id}`}>
          <option value="">What did they do?</option>
          {Object.entries(RESULTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {result && result !== "no_response" && (
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="min-h-[44px] sm:w-44" data-testid={`outcome-date-${account.account_id}`} />
        )}
        <Button onClick={save} disabled={!result || busy || (result !== "no_response" && !date)} className="min-h-[44px] rounded-xl btn-primary" data-testid={`outcome-save-${account.account_id}`}>Save response</Button>
      </div>
    </li>
  );
}

export function OutcomePanel({ doc, onSaved }) {
  if (!doc?.sent_date) return null;
  const accounts = doc.selections?.length ? doc.selections : (doc.library?.source_account_ids || []).map((id) => ({ account_id: id }));
  const recorded = Object.fromEntries((doc.outcomes || []).map((o) => [o.account_id, o]));
  return (
    <section className="mt-4 rounded-2xl border border-purple-200 bg-purple-50/50 p-4" data-testid="outcome-panel">
      <h3 className="text-sm font-semibold text-slate-900">Record the response</h3>
      <p className="text-xs text-slate-600 mt-1">When results arrive, record what happened to each account. Your next-round letters unlock based on this.</p>
      <ul className="mt-3 grid gap-2">
        {accounts.map((a) => <AccountOutcome key={a.account_id} doc={doc} account={a} recorded={recorded[a.account_id]} onSaved={onSaved} />)}
      </ul>
    </section>
  );
}
